/**
 * Production go-live preflight — red/green env board (Tripjack audit C1–C3).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  RefreshCw, ShieldAlert, ShieldCheck, Loader2, AlertTriangle, Server,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/preflight`;

const SEV_STYLE = {
  critical: 'border-rose-200 bg-rose-50',
  high: 'border-amber-200 bg-amber-50',
  info: 'border-gray-200 bg-gray-50',
};

export default function GoLivePreflight() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(API, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load preflight');
      setData(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const checks = data?.checks || [];
  const critical = checks.filter((c) => c.severity === 'critical' && !c.ok);
  const high = checks.filter((c) => c.severity === 'high' && !c.ok);
  const passing = checks.filter((c) => c.ok);

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="go-live-preflight">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2">
            <Server className="w-7 h-7" /> Go-Live Preflight
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Verify Tripjack production URL, Razorpay consistency, company GST, and public domains
            before taking real-money traffic. Set <code className="text-xs bg-gray-100 px-1 rounded">ENVIRONMENT_PROD_STRICT=1</code> on the server to refuse boot on critical failures.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={busy}
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg disabled:opacity-60"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      {err && (
        <p className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</p>
      )}

      {data && (
        <>
          <div
            className={`mb-6 rounded-xl border p-5 flex items-start gap-3 ${
              data.ok ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
            }`}
            data-testid="preflight-summary-banner"
          >
            {data.ok ? (
              <ShieldCheck className="w-8 h-8 text-emerald-700 flex-shrink-0" />
            ) : (
              <ShieldAlert className="w-8 h-8 text-rose-700 flex-shrink-0" />
            )}
            <div>
              <p className={`text-lg font-bold ${data.ok ? 'text-emerald-900' : 'text-rose-900'}`}>
                {data.ok ? 'All critical checks passed' : 'Not ready for production traffic'}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                Critical failures: <strong>{data.critical_failure_count}</strong>
                {' · '}High: <strong>{data.high_failure_count}</strong>
                {' · '}Generated {data.generated_at ? new Date(data.generated_at).toLocaleString('en-IN') : '—'}
              </p>
              {!data.ok && (data.critical_failures?.length > 0) && (
                <p className="text-xs text-rose-800 mt-2 font-mono">
                  {data.critical_failures.join(' · ')}
                </p>
              )}
            </div>
          </div>

          {!data.ok && (
            <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-950">
              <p className="font-semibold flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" /> Typical blockers (audit 2026-05-15)
              </p>
              <ul className="mt-2 list-disc list-inside text-xs space-y-1">
                <li><strong>C1</strong> — <code>TRIPJACK_BASE_URL</code> must be <code>https://tripjack.com</code> (not apitest)</li>
                <li><strong>C2</strong> — Confirm <code>COMPANY_GSTIN</code> and address are real registered values</li>
                <li><strong>C3</strong> — <code>BACKEND_PUBLIC_BASE</code> / <code>FRONTEND_PUBLIC_BASE</code> must be production domains</li>
                <li><strong>ENV_CONSISTENCY</strong> — Do not run Razorpay production + Tripjack UAT together</li>
              </ul>
              <p className="mt-2 text-xs">
                Configure suppliers in{' '}
                <Link to="/admin/supplier-settings" className="underline font-semibold">Supplier Settings</Link>
                {' '}or update server <code>.env</code>, then refresh.
              </p>
            </div>
          )}

          {critical.length > 0 && (
            <section className="mb-6">
              <h2 className="text-sm font-bold text-rose-800 uppercase mb-2">Critical ({critical.length})</h2>
              <div className="space-y-2">
                {critical.map((c) => (
                  <CheckRow key={c.key} row={c} />
                ))}
              </div>
            </section>
          )}

          {high.length > 0 && (
            <section className="mb-6">
              <h2 className="text-sm font-bold text-amber-800 uppercase mb-2">High ({high.length})</h2>
              <div className="space-y-2">
                {high.map((c) => (
                  <CheckRow key={c.key} row={c} />
                ))}
              </div>
            </section>
          )}

          <section>
            <h2 className="text-sm font-bold text-gray-600 uppercase mb-2">
              All checks ({passing.length}/{checks.length} passing)
            </h2>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {checks.map((c) => (
                <CheckRow key={c.key} row={c} compact={c.ok} />
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function CheckRow({ row, compact }) {
  const sev = row.severity || 'info';
  const style = row.ok ? 'border-emerald-100 bg-white' : (SEV_STYLE[sev] || SEV_STYLE.info);
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-xs ${style} ${compact ? 'opacity-80' : ''}`}
      data-testid={`preflight-check-${row.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{row.label}</p>
          <p className="text-gray-600 mt-0.5 font-mono break-all">{row.value}</p>
          {row.message && <p className="text-gray-500 mt-1">{row.message}</p>}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
          row.ok ? 'bg-emerald-100 text-emerald-800' : sev === 'critical' ? 'bg-rose-200 text-rose-900' : 'bg-amber-100 text-amber-900'
        }`}>
          {row.ok ? 'OK' : sev}
        </span>
      </div>
    </div>
  );
}
